// Ctrl/Cmd+C on a FOCUSED field with no text selection copies the field's
// whole value and pops a small "Copied" bubble above it (fades out after ~1s).
// A real selection anywhere keeps native copy semantics untouched, so this
// only adds behaviour where Ctrl+C previously did nothing.

function valueOf(el: HTMLElement): string | null {
  if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) {
    // A partial in-field selection means the user wants native copy.
    try { if (el.selectionStart !== el.selectionEnd) return null; } catch { /* number/date inputs */ }
    return el.value || null;
  }
  if (el instanceof HTMLSelectElement) {
    return el.selectedOptions[0]?.textContent?.trim() || el.value || null;
  }
  // Read-only value boxes / pickers / gridcells — anything focusable that
  // participates in field travel or renders a value as text.
  if (el.hasAttribute('data-travel') || el.matches('[role="gridcell"], dd [tabindex], [contenteditable="true"]')) {
    return el.textContent?.trim() || null;
  }
  return null;
}

function showBubble(anchor: HTMLElement) {
  const r = anchor.getBoundingClientRect();
  const b = document.createElement('div');
  b.textContent = 'Copied';
  b.setAttribute('role', 'status');
  b.className = 'fixed z-system px-2.5 py-1 rounded-full bg-slate-900 text-white text-[0.6875rem] font-medium shadow-lg pointer-events-none select-none';
  b.style.left = `${Math.round(r.left + r.width / 2)}px`;
  b.style.top = `${Math.round(r.top - 6)}px`;
  b.style.transform = 'translate(-50%, -100%) scale(0.8)';
  b.style.opacity = '0';
  b.style.transition = 'opacity 140ms ease-out, transform 180ms cubic-bezier(0.34, 1.56, 0.64, 1)';
  document.body.appendChild(b);
  requestAnimationFrame(() => {
    b.style.opacity = '1';
    b.style.transform = 'translate(-50%, -100%) scale(1)';
  });
  setTimeout(() => {
    b.style.opacity = '0';
    b.style.transform = 'translate(-50%, -130%) scale(0.95)';
  }, 800);
  setTimeout(() => b.remove(), 1100);
}

export function installCopyFocusValue(): () => void {
  const onKeyDown = (e: KeyboardEvent) => {
    if (!(e.ctrlKey || e.metaKey) || (e.key !== 'c' && e.key !== 'C')) return;
    // Any real page selection -> native copy.
    const sel = window.getSelection();
    if (sel && !sel.isCollapsed) return;
    const el = document.activeElement;
    if (!(el instanceof HTMLElement)) return;
    const value = valueOf(el);
    if (!value) return;
    navigator.clipboard?.writeText(value).then(() => showBubble(el)).catch(() => { /* clipboard denied */ });
  };
  document.addEventListener('keydown', onKeyDown);
  return () => document.removeEventListener('keydown', onKeyDown);
}
